import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useProjectElementsGrouped } from '@/hooks/use-project-elements';

export interface SelectedElement {
  name: string;
  category: string;
}

interface ProjectElementsProps {
  selectedElements: string[];
  onElementToggle: (
    elementName: string,
    checked: boolean,
    category?: string
  ) => void;
  className?: string;
}

const preferredCategoryOrder = [
  'Deck Structure',
  'Enclosed Structures',
  'Exterior Finishes',
  'Hardscape Structures',
  'Exterior Enhancements',
  'A-la-Carte',
];

export default function ProjectElements({
  selectedElements,
  onElementToggle,
  className = '',
}: ProjectElementsProps) {
  const { groupedElements, isLoading, error } = useProjectElementsGrouped();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-outline-variant border-t-slate-600 rounded-full animate-spin" />
        <span className="ml-2 text-muted-foreground">Loading project elements...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Failed to load project elements.</p>
        <p className="text-muted-foreground text-sm">Please try again later.</p>
      </div>
    );
  }

  const isElementSelected = (elementName: string) => {
    return selectedElements.includes(elementName);
  };

  const handleElementChange = (
    elementName: string,
    checked: boolean,
    categoryName: string
  ) => {
    onElementToggle(elementName, checked, categoryName);
  };

  return (
    <div className={`grid grid-cols-1 md:grid-cols-3 gap-5 ${className}`}>
      {preferredCategoryOrder.map((category) => {
        const items = groupedElements[category];
        if (!items || items.length === 0) return null;

        return (
          <div key={category} className="space-y-4">
            <h3 className="font-newsreader text-xl font-semibold tracking-tight tracking-tight text-foreground">{category}</h3>
            <div className="space-y-3">
              {items.map((element) => (
                <div key={element.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={element.id}
                    checked={isElementSelected(element.name)}
                    onCheckedChange={(checked) =>
                      handleElementChange(
                        element.name,
                        checked === true,
                        category
                      )
                    }
                  />
                  <Label htmlFor={element.id} className="text-sm">
                    {element.name}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
